import { useState, useEffect, useCallback } from "react";
import { Menu, X } from "lucide-react";
import { useI18n } from "@/i18n/context";
import { pick } from "@/i18n/pick";
import { useNavigate, useLocation } from "react-router-dom";
import { waitAndScrollToAnchor } from "@/lib/scrollToAnchor";
import logo from "@/assets/visionature-logo.webp";

const Navbar = () => {
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
      e.preventDefault();
      if (href.startsWith("/") && !href.startsWith("/#")) {
        navigate(href);
      } else if (href === "#") {
        if (isHome) {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } else {
          navigate("/");
        }
      } else if (href.startsWith("#")) {
        if (isHome) {
          waitAndScrollToAnchor(href);
        } else {
          navigate("/" + href);
        }
      }
    },
    [isHome, navigate]
  );

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-background/95 backdrop-blur-xl backdrop-saturate-150 shadow-[0_1px_0_hsl(var(--border)),0_8px_24px_-12px_rgba(26,26,26,0.08)]"
          : "bg-background/30 backdrop-blur-sm md:bg-transparent md:backdrop-blur-0"
      }`}
    >
      <div className="container mx-auto flex items-center justify-between py-5 px-6 lg:px-8">
        <a href="/" className="flex items-center" aria-label={`Visionature — ${pick(lang, { fr: "Accueil", en: "Home", es: "Inicio" })}`}>
          <img src={logo} alt="Visionature" width={123} height={70} decoding="async" className="h-10 w-auto" />
        </a>

        {/* Desktop */}
        <ul className="hidden md:flex items-center gap-8">
          {t.nav.links.map((l) => {
            const isCatalogue = l.href === "#collection";
            return (
              <li key={l.href}>
                <a
                  href={l.href}
                  onClick={(e) => handleNavClick(e, l.href)}
                  className="text-sm font-sans tracking-wide transition-colors duration-300"
                  style={
                    isCatalogue
                      ? { color: "#C9A13A" }
                      : undefined
                  }
                  onMouseEnter={(e) => {
                    if (isCatalogue) e.currentTarget.style.color = "#B58E2F";
                  }}
                  onMouseLeave={(e) => {
                    if (isCatalogue) e.currentTarget.style.color = "#C9A13A";
                  }}
                >
                  <span className={isCatalogue ? "" : "text-muted-foreground hover:text-foreground"}>
                    {l.label}
                  </span>
                </a>
              </li>
            );
          })}
        </ul>

        <div className="hidden md:flex items-center gap-5">
          {/* Language switcher */}
          <div
            className="flex items-center text-xs font-sans uppercase tracking-widest text-muted-foreground px-1 py-1"
            role="group"
            aria-label="Language selection"
          >
            {(["fr", "en", "es"] as const).map((code, i) => (
              <span key={code} className="flex items-center">
                {i > 0 && <span className="mx-1.5 text-border">/</span>}
                <button
                  onClick={() => setLang(code)}
                  aria-pressed={lang === code}
                  aria-label={`${pick(lang, { fr: "Passer en", en: "Switch language to", es: "Cambiar el idioma a" })} ${code.toUpperCase()}`}
                  className={`transition-colors duration-300 hover:text-foreground ${
                    lang === code ? "text-foreground font-medium" : ""
                  }`}
                >
                  {code.toUpperCase()}
                </button>
              </span>
            ))}
          </div>

          <a
            href="#contact"
            onClick={(e) => handleNavClick(e, "#contact")}
            className="inline-flex items-center px-5 py-2.5 text-sm font-medium bg-primary text-primary-foreground hover:bg-botanical-light transition-colors duration-300 active:scale-[0.97]"
          >
            {t.nav.cta}
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setOpen(!open)}
          className="md:hidden p-2 text-foreground active:scale-95"
          aria-label={pick(lang, { fr: "Ouvrir le menu", en: "Toggle menu", es: "Abrir el menú" })}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden bg-background/95 backdrop-blur-md border-t border-border px-6 pb-6 pt-2">
          <ul className="flex flex-col gap-4">
            {t.nav.links.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  onClick={(e) => {
                    handleNavClick(e, l.href);
                    setOpen(false);
                  }}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
          <div className="mt-4 flex items-center justify-between">
            <a
              href="#contact"
              onClick={(e) => {
                handleNavClick(e, "#contact");
                setOpen(false);
              }}
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium bg-primary text-primary-foreground"
            >
              {t.nav.cta}
            </a>
            <div
              className="flex items-center text-xs font-sans uppercase tracking-widest text-muted-foreground px-1 py-1"
              role="group"
              aria-label="Language selection"
            >
              {(["fr", "en", "es"] as const).map((code, i) => (
                <span key={code} className="flex items-center">
                  {i > 0 && <span className="mx-1.5 text-border">/</span>}
                  <button
                    onClick={() => setLang(code)}
                    aria-pressed={lang === code}
                    aria-label={`${pick(lang, { fr: "Passer en", en: "Switch language to", es: "Cambiar el idioma a" })} ${code.toUpperCase()}`}
                    className={`transition-colors duration-300 hover:text-foreground ${
                      lang === code ? "text-foreground font-medium" : ""
                    }`}
                  >
                    {code.toUpperCase()}
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
